import { SchoolLicense } from '@models/license/school-license';
import { IProductLine } from '@models/product-line';
import { School } from '@models/school';
import {
  Grade
  // eslint-disable-next-line max-len
} from '@shared/branded-reports/branded-benchmark-reports/admin-standards-over-time-report/standards-over-time-report-detail.model';
import { Capabilities } from '@shared/enums/capabilities';
import { CapabilityType } from '@shared/enums/capability-type';

/*
 * Contains functions that help streamline processing of Capabilities.
 *
 * These should be dumb functions that do not have state or external service deps.
 */
export namespace CapabilitiesHelpers {

  export const typeToPropertyMapping = {
    [CapabilityType.Admin]: 'adminCapabilities',
    [CapabilityType.Classroom]: 'classroomCapabilities',
    [CapabilityType.Student]: 'studentCapabilities',
    [CapabilityType.Teacher]: 'teacherCapabilities',
    [CapabilityType.Parent]: 'parentCapabilities',
  };

  /**
   *
   * Looks at all Skus within ProductLines and determines if any of the Product Line Variants have a capability.
   *
   * Returns true if any product line has a sku with the Capability for the specified CapabilityType
   *
   * @returns {boolean}
   */
  export function productLinesHaveCapability(productLines: IProductLine[], capability: Capabilities, capabilityType: CapabilityType): boolean {
    if (!productLines || !productLines.length) {
      return false;
    }

    const allVariants = productLines.flatMap(productLine => productLine.variants ?? []);
    const allSkus = allVariants.flatMap(variant => variant.skus ?? []);

    const capabilityProperty = CapabilitiesHelpers.typeToPropertyMapping[capabilityType];

    const skusWithCapability = allSkus.filter(sku => sku[capabilityProperty]?.includes(capability));


    return !!skusWithCapability?.length;
  }

  export function schoolLicensesHaveCapability(schoolLicenses: SchoolLicense[] | null, capability: Capabilities, capabilityType: CapabilityType): boolean {
    if (schoolLicenses === null || !schoolLicenses || !schoolLicenses.length) {
      return false;
    }

    const capabilityProperty = CapabilitiesHelpers.typeToPropertyMapping[capabilityType];
    const licensesWithCapability = schoolLicenses.filter(license => license[capabilityProperty]?.includes(capability));

    return !!licensesWithCapability.length;
  }

  export function filterSchoolsByBenchmarkReportCapabilities(schools: School[], additionalValidSchool: School = null) : School[] {
    return schools.filter(s => s === additionalValidSchool
      || CapabilitiesHelpers.schoolLicensesHaveCapability(
        s.licenseAssignments,
        Capabilities.BenchmarkReports,
        CapabilityType.Admin));
  }

  export function filterSchoolsByCapabilities(schools: School[], capability: Capabilities, capabilityType: CapabilityType) : School[] {
    return schools.filter(s => CapabilitiesHelpers.schoolLicensesHaveCapability(
      s.licenseAssignments,
      capability,
      capabilityType,));
  }

  export function getGradesFromApplicableProducts(applicableProducts: SchoolLicense[]): Grade[] {
    const grades = Array.from(
      new Map(
        applicableProducts.map(p => [
          `${p.variantType}|${p.variantDescription}`,
          { variant: p.variant, variantDescription: p.variantDescription, productType: p.productType }
        ])
      ).values()
    );

    return [...grades].sort((a, b) => {
      // GradeK always first
      if (a.variant === 'GradeK') return -1;
      if (b.variant === 'GradeK') return 1;

      // Extract base number
      const getBaseNumber = (code: string) => {
        const match = code.match(/^Grade(\d+)/);
        return match ? Number(match[1]) : 9999;
      };

      const numA = getBaseNumber(a.variant);
      const numB = getBaseNumber(b.variant);

      if (numA !== numB) {
        return numA - numB;
      }

      // Same number → use full string (so Grade2 < Grade2C < Grade2M)
      return a.variant.localeCompare(b.variant);
    });
  }
}
